import React from 'react';

import { theme } from 'config/theme';
import { TableShape } from '../context';

interface TableFigureProps {
    width: number;
    height: number;
    seats: number;
    shape: TableShape;
    color?: string;
    filled?: boolean;
    label?: string;
    subLabel?: string;
    isActive?: boolean;
    rotation?: number;
}

const DEFAULT_COLOR = theme.palette.primary.main;
const INACTIVE_COLOR = theme.palette.grey['500'];

/**
 * Pure SVG table figure. The figure is centered at (0,0); the caller must
 * translate to the desired position.
 *
 * All sizing is proportional to `base = min(width, height)` so the figure
 * looks identical at any scale (palette preview vs on-map canvas).
 */
const TableFigure = React.memo(function TableFigure({
    width,
    height,
    seats,
    shape,
    color = DEFAULT_COLOR,
    filled = false,
    label,
    subLabel,
    isActive = true,
    rotation = 0,
}: TableFigureProps) {
    const hw = width / 2;
    const hh = height / 2;
    const r = Math.min(hw, hh); // radius for round/square circle

    // Proportional sizing — all derived from the smaller table dimension so
    // the figure looks the same in the palette preview and on the map canvas.
    const base = Math.min(width, height);
    const chairSize = base * 0.15; // half-dimension of each chair rect (small, clearly distinct)
    const chairGap = base * 0.1; // gap between table edge and chair center
    const tableStroke = Math.max(1, base / 12);
    const centerR = base * 0.39; // center-label circle radius
    const chairRx = chairSize / 3; // subtle corner rounding on chair rects

    const effectiveColor = isActive ? color : INACTIVE_COLOR;
    const tableFill = filled ? effectiveColor : 'white';
    const tableStrokeColor = effectiveColor;
    const chairFill = effectiveColor;

    function renderRoundOrSquareChairs() {
        // Arrange seats radially around the table body
        const chairs: React.ReactNode[] = [];
        const angleStep = (2 * Math.PI) / seats;
        // Place chair centers just outside the table circle radius
        const dist = r + chairGap + chairSize;
        for (let i = 0; i < seats; i++) {
            const angle = -Math.PI / 2 + i * angleStep;
            const cx = Math.cos(angle) * dist;
            const cy = Math.sin(angle) * dist;
            chairs.push(
                <rect
                    key={i}
                    x={cx - chairSize}
                    y={cy - chairSize}
                    width={chairSize * 2}
                    height={chairSize * 2}
                    rx={chairRx}
                    fill={chairFill}
                    stroke="none"
                    opacity={0.85}
                />
            );
        }
        return chairs;
    }

    function renderRectangleChairs() {
        const chairs: React.ReactNode[] = [];
        // 1 chair on each short end; remaining split evenly between top/bottom long sides
        const sideSeats = Math.max(0, seats - 2);
        const topCount = Math.ceil(sideSeats / 2);
        const bottomCount = sideSeats - topCount;

        // Top side chairs — symmetrically centered along the long side
        if (topCount > 0) {
            const spacing = width / (topCount + 1);
            for (let i = 0; i < topCount; i++) {
                const cx = -hw + spacing * (i + 1);
                const cy = -(hh + chairGap + chairSize);
                chairs.push(
                    <rect
                        key={`top-${i}`}
                        x={cx - chairSize}
                        y={cy - chairSize}
                        width={chairSize * 2}
                        height={chairSize * 2}
                        rx={chairRx}
                        fill={chairFill}
                        stroke="none"
                        opacity={0.85}
                    />
                );
            }
        }

        // Bottom side chairs — symmetrically centered along the long side
        if (bottomCount > 0) {
            const spacing = width / (bottomCount + 1);
            for (let i = 0; i < bottomCount; i++) {
                const cx = -hw + spacing * (i + 1);
                const cy = hh + chairGap + chairSize;
                chairs.push(
                    <rect
                        key={`bot-${i}`}
                        x={cx - chairSize}
                        y={cy - chairSize}
                        width={chairSize * 2}
                        height={chairSize * 2}
                        rx={chairRx}
                        fill={chairFill}
                        stroke="none"
                        opacity={0.85}
                    />
                );
            }
        }

        // Left end chair — vertically centered on the short side
        chairs.push(
            <rect
                key="left"
                x={-(hw + chairGap + 2 * chairSize)}
                y={-chairSize}
                width={chairSize * 2}
                height={chairSize * 2}
                rx={chairRx}
                fill={chairFill}
                stroke="none"
                opacity={0.85}
            />
        );

        // Right end chair — vertically centered on the short side
        chairs.push(
            <rect
                key="right"
                x={hw + chairGap}
                y={-chairSize}
                width={chairSize * 2}
                height={chairSize * 2}
                rx={chairRx}
                fill={chairFill}
                stroke="none"
                opacity={0.85}
            />
        );

        return chairs;
    }

    function renderTableBody() {
        if (shape === 'round') {
            return (
                <circle
                    r={r}
                    fill={tableFill}
                    stroke={tableStrokeColor}
                    strokeWidth={tableStroke}
                    strokeDasharray={isActive ? undefined : '4 2'}
                />
            );
        }
        // square or rectangle
        return (
            <rect
                x={-hw}
                y={-hh}
                width={width}
                height={height}
                rx={shape === 'square' ? 4 : 3}
                fill={tableFill}
                stroke={tableStrokeColor}
                strokeWidth={tableStroke}
                strokeDasharray={isActive ? undefined : '4 2'}
            />
        );
    }

    return (
        <g opacity={isActive ? 1 : 0.5}>
            {/* Chairs + table body rotate about the center; the label below stays upright. */}
            <g transform={rotation ? `rotate(${rotation})` : undefined}>
                {shape === 'rectangle' ? renderRectangleChairs() : renderRoundOrSquareChairs()}
                {renderTableBody()}
            </g>

            {/* Center label circle */}
            {label && (
                <>
                    <circle r={centerR} fill={effectiveColor} />
                    {subLabel ? (
                        <>
                            <text
                                textAnchor="middle"
                                y={-centerR * 0.22}
                                dominantBaseline="central"
                                fontSize={centerR * 0.75}
                                fontWeight={700}
                                fill="white"
                                fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
                                style={{ userSelect: 'none', pointerEvents: 'none' }}
                            >
                                {label}
                            </text>
                            <text
                                textAnchor="middle"
                                y={centerR * 0.5}
                                dominantBaseline="central"
                                fontSize={centerR * 0.55}
                                fontWeight={500}
                                fill="rgba(255,255,255,0.82)"
                                fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
                                style={{ userSelect: 'none', pointerEvents: 'none' }}
                            >
                                {subLabel}
                            </text>
                        </>
                    ) : (
                        <text
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize={centerR * 0.85}
                            fontWeight={700}
                            fill="white"
                            fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
                            style={{ userSelect: 'none', pointerEvents: 'none' }}
                        >
                            {label}
                        </text>
                    )}
                </>
            )}
        </g>
    );
});

export default TableFigure;
